import * as React from 'react';
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import TableStyles from '../../documents/Table.module.css';
import PackageDownload from './PackageDownload';
import { PackagePreviewResponse } from '../../../types/packager-types';
import { useAtom } from 'jotai';
import { showPackagerConfigAtom } from '../../../atoms/packager-atoms';

interface PackagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  previewData: PackagePreviewResponse | undefined | null;
  packagerId?: string;
  packageId?: string;
  showDownload?: boolean;
  closePackagerSettings?: boolean;
}

const PackagePreviewModal: React.FC<PackagePreviewModalProps> = ({
  open,
  onClose,
  previewData,
  packagerId,
  packageId,
  showDownload = true,
  closePackagerSettings = false,
}) => {
  const [, setShowPackagerConfig] = useAtom(showPackagerConfigAtom);

  const handleClose = React.useCallback(() => {
    if (closePackagerSettings) {
      setShowPackagerConfig(false);
    }
    onClose();
  }, [closePackagerSettings, onClose, setShowPackagerConfig]);
  const renderObjectHorizontalTable = (obj: Record<string, unknown>) => {
    const keys = Object.keys(obj || {});

    const columnDefs: ColDef[] = keys.map((key) => ({
      field: key,
      headerName: key,
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150,
      flex: 1,
      cellRenderer: (params: any) => {
        const value = params.value;
        if (typeof value === 'object' && value !== null) {
          return (
            <Box sx={{ maxHeight: '100px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.7rem' }}>{JSON.stringify(value, null, 2)}</pre>
            </Box>
          );
        }
        return String(value ?? '');
      },
    }));

    const rowData = [obj];

    return (
      <Box sx={{ height: 'auto', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
          pagination={false}
          domLayout="autoHeight"
          suppressRowClickSelection={true}
          suppressCellFocus={true}
          className="ag-theme-balham"
          rowHeight={32}
          headerHeight={40}
        />
      </Box>
    );
  };

  const renderArrayOfObjectsTable = (arr: Array<Record<string, unknown>>) => {
    const allKeys = Array.from(
      arr.reduce<Set<string>>((set, row) => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach((k) => set.add(k));
        }
        return set;
      }, new Set<string>())
    );

    const columnDefs: ColDef[] = allKeys.map((key) => ({
      field: key,
      headerName: key,
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 120,
      flex: 1,
      cellRenderer: (params: any) => {
        const value = params.value;
        if (value === null || value === undefined) {
          return '-';
        } else if (Array.isArray(value)) {
          return (
            <Box>
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                Array ({value.length} items)
              </Typography>
              <Box sx={{ mt: 1, maxHeight: '100px', overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.7rem' }}>
                  {JSON.stringify(value, null, 2)}
                </pre>
              </Box>
            </Box>
          );
        } else if (typeof value === 'object' && value !== null) {
          return (
            <Box sx={{ maxHeight: '100px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.7rem' }}>{JSON.stringify(value, null, 2)}</pre>
            </Box>
          );
        } else {
          return String(value);
        }
      },
    }));

    return (
      <Box sx={{ height: 'auto', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={arr}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
          suppressRowClickSelection={true}
          suppressCellFocus={true}
          className="ag-theme-balham"
          rowHeight={32}
          headerHeight={40}
        />
      </Box>
    );
  };

  const renderArrayOfPrimitivesTable = (arr: unknown[]) => {
    const columnDefs: ColDef[] = [
      {
        field: 'index',
        headerName: 'Index',
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 80,
        flex: 1,
      },
      {
        field: 'value',
        headerName: 'Value',
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 200,
        flex: 2,
        cellRenderer: (params: any) => {
          const value = params.value;
          if (typeof value === 'object' && value !== null) {
            return (
              <Box sx={{ maxHeight: '100px', overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.7rem' }}>
                  {JSON.stringify(value, null, 2)}
                </pre>
              </Box>
            );
          }
          return String(value ?? '');
        },
      },
    ];

    const rowData = arr.map((value, idx) => ({ index: idx, value: value }));

    return (
      <Box sx={{ height: 'auto', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          pagination={true}
          paginationPageSize={10}
          domLayout="autoHeight"
          suppressRowClickSelection={true}
          suppressCellFocus={true}
          className="ag-theme-balham"
          rowHeight={32}
          headerHeight={40}
        />
      </Box>
    );
  };

  const parseJsonLoose = (content: unknown): unknown | null => {
    if (content && typeof content === 'object') return content;
    if (typeof content !== 'string') return null;

    const tryParse = (s: string): unknown | null => {
      try {
        return JSON.parse(s);
      } catch {
        return null;
      }
    };

    let parsed: unknown | null = tryParse(content);
    if (typeof parsed === 'string') {
      const second = tryParse(parsed);
      if (second !== null) parsed = second;
    }
    if (parsed === null) {
      const firstBrace = content.indexOf('{');
      const lastBrace = content.lastIndexOf('}');
      const firstBracket = content.indexOf('[');
      const lastBracket = content.lastIndexOf(']');
      if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
        parsed = tryParse(content.slice(firstBrace, lastBrace + 1));
      }
      if (
        parsed === null &&
        firstBracket !== -1 &&
        lastBracket !== -1 &&
        lastBracket > firstBracket
      ) {
        parsed = tryParse(content.slice(firstBracket, lastBracket + 1));
      }
    }
    return parsed;
  };

  const renderContentAsTable = (content: unknown) => {
    const parsed = parseJsonLoose(content);
    if (parsed === null) {
      return (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {typeof content === 'string' ? content : String(content ?? '')}
        </pre>
      );
    }
    if (Array.isArray(parsed)) {
      const isArrayOfObjects = parsed.every(
        (item) => item !== null && typeof item === 'object' && !Array.isArray(item)
      );
      return isArrayOfObjects
        ? renderArrayOfObjectsTable(parsed as Array<Record<string, unknown>>)
        : renderArrayOfPrimitivesTable(parsed as unknown[]);
    }
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      return renderObjectHorizontalTable(obj);
    }
    return (
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
        {typeof content === 'string' ? content : String(content ?? '')}
      </pre>
    );
  };

  return (
    <DialogGlynt
      width="800px"
      open={open}
      closeModal={handleClose}
      cypressId={`dialog-package-preview`}
    >
      <DialogTitleGlynt title="Package Preview" closeModal={handleClose} />
      <DialogContentGlynt>
        <Box display="flex" flexDirection="column" gap={2}>
          {previewData?.delivery_data_grouping_strategy && (
            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="body2">
                Delivery Data Grouping Strategy: {previewData.delivery_data_grouping_strategy}
              </Typography>
              {showDownload && packagerId && packageId ? (
                <PackageDownload packagerId={packagerId} packageId={packageId} />
              ) : null}
            </Box>
          )}

          {previewData?.data?.map((entry: any, entryIdx: number) => {
            const effectiveFilename = entry?.filename;
            const effectiveContent = entry?.content;
            const effectiveSourceFiles = entry?.source_files;

            return (
              <Box key={`preview-entry-${entryIdx}`}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    {effectiveFilename || 'Untitled file'}
                  </Typography>
                  {effectiveContent == null ? (
                    <Typography variant="body2">No content</Typography>
                  ) : (
                    (() => {
                      if (typeof effectiveContent === 'string') {
                        try {
                          const parsed = JSON.parse(effectiveContent);
                          if (
                            parsed &&
                            typeof parsed === 'object' &&
                            parsed.data &&
                            Array.isArray(parsed.data)
                          ) {
                            return renderContentAsTable(parsed.data);
                          }
                        } catch (e) {
                          console.error('Failed to parse content JSON:', e);
                        }
                      }
                      return renderContentAsTable(effectiveContent);
                    })()
                  )}
                </Box>

                <Box sx={{ marginTop: '20px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Source Files
                  </Typography>
                  {effectiveSourceFiles && effectiveSourceFiles.length > 0 ? (
                    <Paper className={TableStyles.documentsTablePaper}>
                      <Table className={TableStyles.documentsTable} size="small">
                        <TableHead className={TableStyles.documentsTableHead}>
                          <TableRow className={TableStyles.documentsTableHeadRow}>
                            <TableCell className={TableStyles.documentsTableHeadCell}>
                              File Name
                            </TableCell>
                            <TableCell className={TableStyles.documentsTableHeadCell}>
                              Content Type
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {effectiveSourceFiles.map((sf: any, idx: number) => (
                            <TableRow
                              key={`${sf.filename}-${idx}`}
                              className={TableStyles.documentsTableRow}
                            >
                              <TableCell className={TableStyles.documentsTableRowCell}>
                                {sf.filename || '-'}
                              </TableCell>
                              <TableCell className={TableStyles.documentsTableRowCell}>
                                {sf.content_type || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  ) : (
                    <Typography variant="body2">No source files</Typography>
                  )}
                </Box>

                {entryIdx < (previewData?.data?.length || 0) - 1 ? (
                  <Divider sx={{ mt: 4, mb: 1 }} />
                ) : null}
              </Box>
            );
          })}
        </Box>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleClose}
        closeModal={handleClose}
        cancelCypressId={`btn-close-preview-cancel`}
        submitCypressId={`btn-close-preview`}
        clearCypressId={`btn-close-preview-clear`}
        showCancelButton={false}
        submitButtonText="Close"
      />
    </DialogGlynt>
  );
};

export default PackagePreviewModal;
